import type { FilterCondition, FilterableFarmProfile } from './types';

/**
 * Set view of a profile value for the set operators.
 *
 * Array fields are their own set. A scalar string field (`ecoStatus`) is a
 * one-element set, so `includesAny`/`excludesAny` mean "is one of" / "is none
 * of" on it (AG-275). `null`/`undefined` is the empty set, matching how an
 * empty array field already behaves. Numbers have no set view.
 */
function asSet(profileValue: unknown): string[] | null {
  if (Array.isArray(profileValue)) return profileValue as string[];
  if (typeof profileValue === 'string') return [profileValue];
  if (profileValue === null || profileValue === undefined) return [];
  return null;
}

/**
 * Evaluate a single FilterCondition against a farm profile.
 * Returns true when the condition PASSES (its constraint is met).
 *
 * Supported operators:
 * - `includesAny` — profile set shares at least one element with value[].
 * - `includesAll` — profile set contains every element in value[].
 * - `excludesAny` — profile set shares NONE of value[] (passes when none present).
 * - `isEmpty`     — profile array/scalar field is null, undefined, or empty.
 * - `eq`          — profile scalar field equals value (strict equality).
 * - `gte`         — profile numeric field >= value.
 * - `lte`         — profile numeric field <= value.
 *
 * The set operators see array fields as-is and a scalar string field as a
 * one-element set (see `asSet`), so `ecoStatus excludesAny ['Konventionell']`
 * is the durable way to express "any organic production".
 *
 * A requirement or field is VISIBLE when every condition passes (AND), and HIDDEN
 * as soon as one fails — the first failing condition supplies the hide reason.
 *
 * @param condition The condition to evaluate.
 * @param profile The farm profile to evaluate it against.
 * @returns true when the condition is satisfied.
 */
export function evaluateCondition(
  condition: FilterCondition,
  profile: FilterableFarmProfile,
): boolean {
  const { field, operator, value } = condition;
  // `field` is constrained to keys of FilterableFarmProfile, so this lookup is
  // total; the widening cast only tells the compiler the index is safe.
  const profileValue: unknown = (profile as unknown as Record<string, unknown>)[field];

  switch (operator) {
    case 'includesAny': {
      if (!Array.isArray(value)) return false;
      const set = asSet(profileValue);
      if (!set) return false;
      return value.some((v) => set.includes(v));
    }

    case 'includesAll': {
      if (!Array.isArray(value)) return false;
      const set = asSet(profileValue);
      if (!set) return false;
      return value.every((v) => set.includes(v));
    }

    case 'excludesAny': {
      if (!Array.isArray(value)) return false;
      const set = asSet(profileValue);
      // No set view (a number) → shares none of value[] → condition passes.
      if (!set) return true;
      return !value.some((v) => set.includes(v));
    }

    case 'isEmpty': {
      if (profileValue === null || profileValue === undefined) return true;
      if (Array.isArray(profileValue)) return profileValue.length === 0;
      return false;
    }

    case 'eq':
      return profileValue === value;

    case 'gte': {
      if (typeof profileValue !== 'number') return false;
      return profileValue >= (value as number);
    }

    case 'lte': {
      if (typeof profileValue !== 'number') return false;
      return profileValue <= (value as number);
    }

    default:
      // Unknown operator — treat as unmet so misconfigured rules surface as a
      // visible requirement rather than silently hiding one.
      return false;
  }
}
